// @flow

/* eslint no-undef: 0 */
/* eslint prefer-rest-params: 0 */
/* eslint wrap-iife: 0 */
/* eslint no-unused-expressions: 0 */
/* eslint no-multi-assign: 0 */
/* eslint prefer-spread: 0 */

// ^^ eslint rules need to be disabled for 3rd party JS snippets that we use in this module.
// changing the syntax might lead to unexpected behaviour, and we should avoid doing that

/**
 * Provides methods allowing for the tracking of business acquisition from a
 * number of outside campaigns.
 */
class BusinessAcquisitionTracking {
  /**
   * An array of all the conversions that were requested to be reported before
   * the Google scripts were loaded.
   * @see reportGoogleConversion for information about the object's properties
   */
  static queuedGoogleConversions = [];

  /**
   * An array of all the conversions that were requested to be reported before
   * the Facebook scripts were loaded.
   * @see reportFacebookConversion for information about the object's properties
   */
  static queuedFacebookConversions = [];

  /**
   * An array of all the conversions that were requested to be reported before
   * the Pinterest scripts were loaded.
   * @see reportPinterestConversion for information about the object's properties
   */
  static queuedPinterestConversions = [];

  /**
   * An array of all the conversions that were requested to be reported before
   * the Twitter scripts were loaded.
   * @see reportTwitterConversion for information about the object's properties
   */
  static queuedTwitterConversions = [];

  /**
   * Reports a conversion for the specified URL to Google. If Google tracking
   * scripts have not been inserted and loaded by
   * BusinessAcquisitionTracking.insertGoogleConversionTag, this function pushes conversion to array.
   */
  static reportGoogleConversion(accountId: number, conversionLabel: string, redirectUrl: ?string) {
    if (typeof window.gtag_report_conversion === 'undefined') {
      BusinessAcquisitionTracking.queuedGoogleConversions.push({
        id: accountId,
        label: conversionLabel,
        url: redirectUrl,
      });
      return;
    }

    // A function defined by the Google tracking scripts.
    window.gtag_report_conversion(accountId, conversionLabel, redirectUrl);
  }

  /**
   * Reports the specified standard event
   * (https://www.facebookmarketingdevelopers.com/pixels/up) to Facebook.
   * If Facebook Pixel scripts have not been inserted and loaded by
   * BusinessAcquisitionTracking.insertFacebookPixelTag, this function pushes conversion to array.
   */
  static reportFacebookConversion(advertiserId: string, event: string, data: ?Object) {
    if (typeof window.fbq === 'undefined') {
      BusinessAcquisitionTracking.queuedFacebookConversions.push({
        id: advertiserId,
        event,
        data,
      });
      return;
    }

    // A function defined by the Facebook tracking scripts.
    if (!data) {
      window.fbq('track', event);
    } else {
      window.fbq('track', event, data);
    }
  }

  /**
   * Reports the specified standard event to Pinterest.
   * If Pinterest tag tracking scripts have not been inserted and loaded by
   * BusinessAcquisitionTracking.insertPinterestTag, this function pushes conversion to array.
   */
  static reportPinterestConversion(tagId: string, event: string, data: ?Object) {
    if (typeof window.pintrk === 'undefined') {
      BusinessAcquisitionTracking.queuedPinterestConversions.push({
        id: tagId,
        event,
        data,
      });
      return;
    }

    // A function defined by the Pinterest tracking scripts.
    if (!data) {
      window.pintrk('track', event);
    } else {
      window.pintrk('track', event, data);
    }
  }

  /**
   * Reports the specified conversion to Twitter.
   * If Twitter conversion scripts have not been inserted and loaded by
   * BusinessAcquisitionTracking.insertTwitterConversionTag, this function pushes conversion to array.
   */
  static reportTwitterConversion(conversionId: string, data: ?Object) {
    if (typeof window.twttr === 'undefined') {
      BusinessAcquisitionTracking.queuedTwitterConversions.push({
        id: conversionId,
        data,
      });
      return;
    }

    // A function defined by the Twitter Universal Tag scripts.
    if (!data) {
      window.twttr.conversion.trackPid(conversionId);
    } else {
      window.twttr.conversion.trackPid(conversionId, data);
    }
  }

  /**
   * Reports a conversion for the specified conversion ID to The Trade Desk.
   */
  static reportTheTradeDeskConversion(advertiserId: string, conversionId: string) {
    const img = document.createElement('img');
    img.setAttribute('height', '1');
    img.setAttribute('width', '1');
    img.style.borderStyle = 'none';
    img.style.position = 'absolute';
    img.setAttribute(
      'src',
      `//insight.adsrvr.org/track/conv/?adv=${advertiserId}&ct=0:${conversionId}&fmt=3`,
    );
    // $FlowFixMe: need to check that document.body is not null
    document.body.insertBefore(img, document.body.lastChild);
  }

  /**
   * Inserts scripts at the bottom of the current page's body allowing
   * for tracking of conversions in LinkedIn Ads.
   */
  static reportLinkedInConversion(partnerId: string, conversionId: string) {
    const img = document.createElement('img');
    img.setAttribute('width', '1');
    img.setAttribute('height', '1');
    img.setAttribute(
      'src',
      `https://dc.ads.linkedin.com/collect/?pid=${partnerId}` +
        `&conversionId=${conversionId}&fmt=gif`,
    );
    img.style.display = 'none';
    // $FlowFixMe: need to check that document.body is not null
    document.body.insertBefore(img, document.body.lastChild);
  }

  /**
   * Inserts scripts at the bottom of the current page's body allowing
   * for tracking of conversions in DoubleClick Ads.
   */
  static reportDoubleClickConversion(tagId: string, type: string, category: string) {
    const ord = Math.random() * 10000000000000;
    const iframe = document.createElement('iframe');
    iframe.setAttribute('width', '1');
    iframe.setAttribute('height', '1');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute(
      'src',
      `https://${tagId}.fls.doubleclick.net/activityi;src=${tagId};
    type=${type};cat=${category};dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;ord=${ord}?`,
    );
    iframe.style.display = 'none';
    // $FlowFixMe: need to check that document.body is not null
    document.body.insertBefore(iframe, document.body.lastChild);
  }

  /**
   * Inserts script at the top of the current page's body allowing
   * for tracking of visitors in Google AdWords. After the scripts
   * load, a call to BusinessAcquisitionTracking.reportGoogleConversion will
   * actually send events to Google.
   */
  static insertGoogleAdWordsTag(accountId: number) {
    const script1 = document.createElement('script');
    script1.setAttribute('async', '');
    script1.setAttribute('src', `https://www.googletagmanager.com/gtag/js?id=AW-${accountId}`);
    script1.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(a, b, c) {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', 'AW-' + accountId);
      window.gtag_report_conversion = function(accountId, label, url) {
        const callback = function() {
          if (typeof url !== 'undefined') {
            window.location.assign(url);
          }
        };
        window.gtag('event', 'conversion', {
          send_to: 'AW-' + accountId + '/' + label,
          event_callback: callback,
        });
        return false;
      };
      const queuedConversions = BusinessAcquisitionTracking.queuedGoogleConversions;
      for (let i = 0; i < queuedConversions.length; i += 1) {
        const conversion = queuedConversions[i];
        if (conversion.id === accountId) {
          BusinessAcquisitionTracking.reportGoogleConversion(
            conversion.id,
            conversion.label,
            conversion.url,
          );
        }
      }
    };
    // $FlowFixMe: need to check that document.body is not null
    document.body.insertBefore(script1, document.body.firstChild);
  }

  /**
   * Inserts scripts at the top of the current page's body allowing
   * for tracking of visitors in Facebook Ads. After the scripts
   * load, a call to BusinessAcquisitionTracking.reportFacebookConversion will
   * actually send events to Facebook.
   */
  static insertFacebookPixelTag(advertiserId: string) {
    !(function(f, b, e, v, n, t, s) {
      if (f.fbq) {
        return;
      }
      n = f.fbq = function() {
        // $FlowFixMe: need to check that n is defined
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) {
        f._fbq = n;
      }
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      // $FlowFixMe: need to check that s.parentNode is not null
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', '//connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', advertiserId);
    window.fbq('track', 'PageView');
    const queuedConversions = BusinessAcquisitionTracking.queuedFacebookConversions;
    for (let i = 0; i < queuedConversions.length; i += 1) {
      const conversion = queuedConversions[i];
      if (conversion.id === advertiserId) {
        BusinessAcquisitionTracking.reportFacebookConversion(
          advertiserId,
          conversion.event,
          conversion.data,
        );
      }
    }
  }

  /**
   * Inserts scripts at the top of the current page's body allowing
   * for tracking of visitors in Twitter Ads.
   */
  static insertTwitterUniversalTag(universalTagId: string) {
    !(function(e, t, n, s, u, a) {
      e.twq ||
        ((s = e.twq = function() {
          // $FlowFixMe: need to check that s is defined
          s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
        }),
        (s.version = '1.1'),
        (s.queue = []),
        (u = t.createElement(n)),
        (u.async = !0),
        (u.src = '//static.ads-twitter.com/uwt.js'),
        (a = t.getElementsByTagName(n)[0]),
        // $FlowFixMe: need to check that a.parentNode is not null
        a.parentNode.insertBefore(u, a));
    })(window, document, 'script');
    window.twq('init', universalTagId);
    window.twq('track', 'PageView');
  }

  /**
   * Inserts scripts at the top of the current page's body allowing
   * for tracking of visitors in LinkedIn Ads.
   */
  static insertLinkedInInsightTag(partnerId: string) {
    window._linkedin_data_partner_id = partnerId;
    const script = document.createElement('script');
    script.setAttribute('src', '//snap.licdn.com/li.lms-analytics/insight.min.js');
    // $FlowFixMe: need to check that document.body is not null
    document.body.insertBefore(script, document.body.firstChild);
  }

  /**
   * Inserts script at the top of the current page's body allowing
   * for tracking of visitors in Pinterest Ads. After the script
   * load, a call to BusinessAcquisitionTracking.reportPinterestConversion will
   * actually send events to Pinterest.
   *
   */
  static insertPinterestTag(tagId: string) {
    !(function(e) {
      if (!window.pintrk) {
        window.pintrk = function() {
          window.pintrk.queue.push(Array.prototype.slice.call(arguments));
        };
        const n = window.pintrk;
        n.queue = [];
        n.version = '3.0';
        const t = document.createElement('script');
        t.async = !0;
        t.src = e;
        const r = document.getElementsByTagName('script')[0];
        // $FlowFixMe: need to check that r and r.parentNode are not null
        r.parentNode.insertBefore(t, r);
      }
    })('https://s.pinimg.com/ct/core.js');
    window.pintrk('load', tagId);
    window.pintrk('page');
    const queuedConversions = BusinessAcquisitionTracking.queuedPinterestConversions;
    for (let i = 0; i < queuedConversions.length; i += 1) {
      const conversion = queuedConversions[i];
      if (conversion.id === tagId) {
        BusinessAcquisitionTracking.reportPinterestConversion(
          tagId,
          conversion.event,
          conversion.data,
        );
      }
    }
  }
}

export default BusinessAcquisitionTracking;
